/**
 * The read-modify-write rules behind inline editing of actions.
 *
 * Action updates are full-body PUTs with no server-side copy-forward, so a
 * partial body nulls every column it omits. Both list endpoints return a
 * superset of what the PUT writes, which is what makes editing one cell
 * possible: take the row the table already holds, drop the keys the write path
 * must not see, override the field the user touched, and send the whole thing.
 *
 * Everything here is pure so the stripping rules can be tested directly; the
 * mutation and cache work live in `useActionFieldEdit`.
 */

import dayjs from 'dayjs';
import {
  ACTION_PRIORITY_OPTIONS,
  ACTION_STATUS_OPTIONS,
  ActionAssigneeKind,
  DEFAULT_ACTION_PRIORITY,
} from '../ICSConstants';
import type { RosterEntry } from '../hooks/incidentRoster/incidentRoster.types';

/**
 * Keys that must not be echoed back to the PUT.
 *
 * `depends_on` is the one that bites: it is not derived, and the write path
 * acts on it by deleting and re-inserting the dependency rows, so echoing it
 * makes every unrelated cell edit churn the dependency table. It is stripped
 * here and re-added through the patch when dependencies are what is being
 * edited.
 *
 * The rest are read-only join output (`blocked_by`, `resource_details`,
 * `poc_user_name`, `user_name`), row identity of a version that is about to be
 * superseded (`version`, `timestamp`), or presentation noise (`index`).
 */
export const DERIVED_ACTION_KEYS = [
  'blocked_by',
  'depends_on',
  'index',
  'aor_name',
  'poc_user_name',
  'resource_details',
  'workspace_name',
  'timestamp',
  'user_name',
  'version',
] as const;

const DERIVED_KEY_SET: ReadonlySet<string> = new Set(DERIVED_ACTION_KEYS);

/** The action row minus everything the write path must not receive. */
export function stripDerivedActionKeys(
  action: Record<string, unknown>
): Record<string, unknown> {
  const kept: Record<string, unknown> = {};
  Object.keys(action).forEach((key) => {
    if (!DERIVED_KEY_SET.has(key)) kept[key] = action[key];
  });
  return kept;
}

/**
 * The full PUT body for changing one or more fields of an existing action.
 *
 * The patch is applied last so it can deliberately reinstate a stripped key,
 * which is how a dependency edit sends `depends_on`.
 */
export function actionEditBody(
  action: Record<string, unknown>,
  patch: Record<string, unknown>
): Record<string, unknown> {
  return { ...stripDerivedActionKeys(action), ...patch };
}

export type ActionAssigneePatch = {
  assignee_kind: ActionAssigneeKind | null;
  assignee_id: string | null;
  poc: string | null;
};

/**
 * All three assignee fields, always written together.
 *
 * `poc` stays the source of truth for user assignments (the ICS-233 export and
 * several queries join on it), so coms keeps the pair in lockstep and clears
 * `poc` for a team or position. Writing all three means clearing an assignment
 * actually clears it instead of letting the untouched arm resurrect it.
 */
export function assigneePatch(
  kind: ActionAssigneeKind | null | undefined,
  id: string | null | undefined
): ActionAssigneePatch {
  if (!kind || !id) {
    return { assignee_kind: null, assignee_id: null, poc: null };
  }
  return {
    assignee_kind: kind,
    assignee_id: id,
    poc: kind === 'user' ? id : null,
  };
}

export type AssigneeOption = { label: string; value: string };

/** The option lists the drawer already fetches, reused to resolve names. */
export type AssigneeDirectory = {
  users: AssigneeOption[];
  teams: AssigneeOption[];
  positions: AssigneeOption[];
  /** Incident roster used to resolve occupants of a position assignee. */
  roster?: RosterEntry[];
};

export const EMPTY_ASSIGNEE_DIRECTORY: AssigneeDirectory = {
  users: [],
  teams: [],
  positions: [],
};

export type ResolvedAssignee = {
  kind: ActionAssigneeKind | null;
  id: string | null;
  label: string;
  /** An assignment exists but its name is not in the directory yet. */
  unresolved: boolean;
};

export const ASSIGNEE_KIND_LABEL: Record<ActionAssigneeKind, string> = {
  user: 'Person',
  team: 'Team',
  position: 'ICS Position',
  role: 'Role',
};

const UNRESOLVED_LABEL: Record<ActionAssigneeKind, string> = {
  user: 'Assigned person',
  team: 'Assigned team',
  position: 'Assigned position',
  role: 'Assigned role',
};

export const UNASSIGNED_LABEL = 'Unassigned';

type AssignableAction = {
  assignee_kind?: string | null;
  assignee_id?: string | null;
  poc?: string | null;
  poc_user_name?: string | null;
};

/**
 * Who owns this task, as a name rather than an id.
 *
 * The table used to render `poc_user_name` alone, so every team and position
 * assignment read as "Unassigned" — coms clears `poc` for those on purpose.
 * Actions written before the assignee model existed carry only `poc`, which is
 * a user assignment by definition.
 */
export function resolveActionAssignee(
  action: AssignableAction,
  directory: AssigneeDirectory = EMPTY_ASSIGNEE_DIRECTORY
): ResolvedAssignee {
  const kind = (action.assignee_kind ??
    (action.poc ? 'user' : null)) as ActionAssigneeKind | null;
  const id = action.assignee_id ?? action.poc ?? null;

  if (!kind || !id) {
    return { kind: null, id: null, label: UNASSIGNED_LABEL, unresolved: false };
  }

  const options =
    kind === 'team'
      ? directory.teams
      : kind === 'position'
        ? directory.positions
        : kind === 'role'
          ? []
          : directory.users;
  const match = options.find((option) => option.value === id);
  if (match) return { kind, id, label: match.label, unresolved: false };

  // The server's COALESCE already resolved a user assignment for us.
  const pocName = action.poc_user_name?.trim();
  if (kind === 'user' && pocName) {
    return { kind, id, label: pocName, unresolved: false };
  }

  return { kind, id, label: UNRESOLVED_LABEL[kind], unresolved: true };
}

/** Directory entries grouped for the assignee picker, empty groups dropped. */
export function assigneeGroups(
  directory: AssigneeDirectory
): { kind: ActionAssigneeKind; label: string; items: AssigneeOption[] }[] {
  return (['user', 'team', 'position'] as const)
    .map((kind) => ({
      kind,
      label:
        kind === 'user'
          ? 'People'
          : kind === 'team'
            ? 'Teams'
            : 'ICS Positions',
      items:
        kind === 'user'
          ? directory.users
          : kind === 'team'
            ? directory.teams
            : directory.positions,
    }))
    .filter((group) => group.items.length > 0);
}

/** Open work past its target date. A Closed action is done, however late. */
export function isActionOverdue(
  status: string | null | undefined,
  targetDate: Date | string | null | undefined,
  now: Date = new Date()
): boolean {
  if (!targetDate || status === 'Closed') return false;
  const target = new Date(targetDate);
  if (Number.isNaN(target.getTime())) return false;
  return target < now;
}

export const ACTION_STATUS_VALUES = ACTION_STATUS_OPTIONS.map(
  (option) => option.value
);

export const ACTION_PRIORITY_VALUES = ACTION_PRIORITY_OPTIONS.map(
  (option) => option.value
);

/** Most urgent first, which is the order triage wants. */
const PRIORITY_RANK: Record<string, number> = {
  Critical: 0,
  High: 1,
  Normal: 2,
  Low: 3,
};

export function actionPriorityRank(
  priority: string | null | undefined
): number {
  return (
    PRIORITY_RANK[priority ?? DEFAULT_ACTION_PRIORITY] ?? PRIORITY_RANK.Normal
  );
}

export function compareActionPriority(
  a: string | null | undefined,
  b: string | null | undefined
): number {
  return actionPriorityRank(a) - actionPriorityRank(b);
}

export type ActionBadgeVariant =
  | 'blue'
  | 'amber'
  | 'green'
  | 'orange'
  | 'indigo'
  | 'violet'
  | 'teal'
  | 'secondary'
  | 'destructive'
  | 'outline';

/** Shadcn Badge variant per priority, shared by the table and the board. */
export function actionPriorityBadgeVariant(
  priority: string | null | undefined
): ActionBadgeVariant {
  switch (priority) {
    case 'Critical':
      return 'destructive';
    case 'High':
      return 'orange';
    case 'Low':
      return 'outline';
    default:
      return 'secondary';
  }
}

export function actionStatusBadgeVariant(
  status: string | null | undefined
): ActionBadgeVariant {
  switch (status) {
    case 'Planned':
      return 'blue';
    case 'Current/Ongoing':
      return 'amber';
    case 'Blocked':
      return 'destructive';
    case 'Submitted for Review':
      return 'violet';
    case 'Approved':
      return 'teal';
    case 'Closed':
      return 'green';
    default:
      return 'secondary';
  }
}

/** Timeline bar fill — Badge variants are too light to read as a bar. */
export function actionStatusBarClass(
  status: string | null | undefined
): string {
  switch (status) {
    case 'Planned':
      return 'tw:bg-blue-600';
    case 'Current/Ongoing':
      return 'tw:bg-amber-600';
    case 'Blocked':
      return 'tw:bg-rose-600';
    case 'Submitted for Review':
      return 'tw:bg-violet-600';
    case 'Approved':
      return 'tw:bg-emerald-600';
    case 'Closed':
      return 'tw:bg-green-600';
    default:
      return 'tw:bg-muted-foreground';
  }
}

const DATE_TIME_LOCAL_FORMAT = 'YYYY-MM-DDTHH:mm';

export const ACTION_DATE_DISPLAY_FORMAT = 'MM/DD/YYYY HH:mm';

/** Stored UTC instant to the local wall-clock string a date input wants. */
export function toDateTimeLocalInput(
  value: Date | string | null | undefined
): string {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(DATE_TIME_LOCAL_FORMAT) : '';
}

/** The inverse: a date input's local wall-clock string back to a UTC instant. */
export function fromDateTimeLocalInput(value: string): string | null {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toISOString() : null;
}

export function formatActionDate(
  value: Date | string | null | undefined
): string {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(ACTION_DATE_DISPLAY_FORMAT) : '—';
}
